import { Injectable } from '@angular/core';
import { AppState } from '@app/core/states/app.state';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { PublishingData } from '../models/publishing.model';
import { PublishingService } from '../services/publishing.service';
import {
  FetchPublishingDataError,
  FetchPublishingDataRequest,
  FetchPublishingDataSuccess,
  TogglePublishingBannerCollapsed,
  UpdateCompletedStepError,
  UpdateCompletedStepRequest,
  UpdateCompletedStepSuccess,
} from './publishing.actions';

export interface PublishingStateModel {
  publishingData: PublishingData;
  publishingBannerCollapsed: boolean;
  isLoading: boolean;
  error: any;
}

@State<PublishingStateModel>({
  name: 'publishing',
  defaults: {
    publishingData: null,
    publishingBannerCollapsed: false,
    isLoading: false,
    error: null,
  },
})
@Injectable()
export class PublishingState {
  constructor(
    private readonly publishingService: PublishingService,
    private readonly store: Store,
  ) {}

  @Selector()
  static publishingData(state: PublishingStateModel) {
    return state.publishingData;
  }

  @Selector()
  static appName(state: PublishingStateModel) {
    return state.publishingData?.name;
  }

  @Selector()
  static appLogo(state: PublishingStateModel) {
    return state.publishingData?.logo;
  }

  @Selector()
  static iosPublishingData(state: PublishingStateModel) {
    return state.publishingData?.ios || null;
  }

  @Selector()
  static noIosPublishingData(state: PublishingStateModel) {
    return state.publishingData?.ios === null || state.publishingData?.ios === undefined;
  }

  @Selector()
  static androidPublishingData(state: PublishingStateModel) {
    return state.publishingData?.android || null;
  }

  @Selector()
  static androidScreenshot(state: PublishingStateModel) {
    return state.publishingData?.android?.screenshot || null;
  }

  @Selector()
  static playStoreLink(state: PublishingStateModel) {
    return state.publishingData?.android?.storeUrl || null;
  }

  @Selector()
  static iosScreenshot(state: PublishingStateModel) {
    return state.publishingData?.ios?.screenshot || null;
  }

  @Selector()
  static appStoreLink(state: PublishingStateModel) {
    return state.publishingData?.ios?.storeUrl || null;
  }

  @Selector()
  static isLoading(state: PublishingStateModel) {
    return state.isLoading;
  }

  @Selector()
  static error(state: PublishingStateModel) {
    return state.error;
  }

  @Selector()
  static publishingBannerCollapsed(state: PublishingStateModel): boolean {
    return state.publishingBannerCollapsed;
  }

  @Selector()
  static planId(state: PublishingStateModel): number {
    return state.publishingData?.planId;
  }

  @Action(FetchPublishingDataRequest)
  fetchPublishingDataRequest(
    ctx: StateContext<PublishingStateModel>,
    { appId }: FetchPublishingDataRequest,
  ) {
    ctx.patchState({
      isLoading: true,
      error: null,
    });

    return this.publishingService.getPublicationStatus(appId).pipe(
      tap(publishingData => {
        ctx.dispatch(new FetchPublishingDataSuccess(publishingData));
      }),
      catchError(error => {
        ctx.dispatch(new FetchPublishingDataError(error));

        return of(error);
      }),
    );
  }

  @Action(FetchPublishingDataSuccess)
  fetchPublishingDataSuccess(
    ctx: StateContext<PublishingStateModel>,
    { publishingData }: FetchPublishingDataSuccess,
  ) {
    ctx.patchState({
      publishingData,
      isLoading: false,
      error: null,
    });
  }

  @Action(FetchPublishingDataError)
  fetchPublishingDataError(
    ctx: StateContext<PublishingStateModel>,
    { error }: FetchPublishingDataError,
  ) {
    ctx.patchState({
      isLoading: false,
      error,
    });
  }

  @Action(UpdateCompletedStepRequest)
  updateCompletedStepRequest(
    ctx: StateContext<PublishingStateModel>,
    { appId, platform, step }: UpdateCompletedStepRequest,
  ) {
    ctx.patchState({
      isLoading: true,
      error: null,
    });

    return this.publishingService.updateCompletedStep(appId, platform, step).pipe(
      tap(() => {
        ctx.dispatch(new UpdateCompletedStepSuccess());
      }),
      catchError(error => {
        ctx.dispatch(new UpdateCompletedStepError(error));

        return of(error);
      }),
    );
  }

  @Action(UpdateCompletedStepSuccess)
  updateCompletedStepSuccess(ctx: StateContext<PublishingStateModel>) {
    ctx.patchState({
      isLoading: false,
      error: null,
    });

    const appId = this.store.selectSnapshot(AppState.currentAppId);
    if (appId) {
      ctx.dispatch(new FetchPublishingDataRequest(appId));
    }
  }

  @Action(UpdateCompletedStepError)
  updateCompletedStepError(
    ctx: StateContext<PublishingStateModel>,
    { error }: UpdateCompletedStepError,
  ) {
    ctx.patchState({
      isLoading: false,
      error,
    });
  }

  @Action(TogglePublishingBannerCollapsed)
  togglePublishingBannerCollapsed(ctx: StateContext<PublishingStateModel>) {
    const state = ctx.getState();
    ctx.patchState({
      publishingBannerCollapsed: !state.publishingBannerCollapsed,
    });
  }
}
